import { BookSeries, BookSeriesItemsForm, BookSeriesItem } from "./book-series";
import GDropdown from "./g-dropdown";
import GModal from "./g-modal";
import { formatMoney } from "./g-utils";
import { CarouselManager } from "../utils/carouselManager";

class SeriesCarouselModal extends GModal {
  constructor() {
    super();

    this.form = this.querySelector("form");
  }

  open() {
    const result = super.open();

    this.items.forEach((item) => item.enable());
    this.form.dispatchEvent(new CustomEvent("series-carousel-items:update"));

    return result;
  }

  get items() {
    return this.querySelectorAll("series-carousel-item");
  }
}

class SeriesCarousel extends BookSeries {
  constructor() {
    super();

    this.modal = this.querySelector("series-carousel-modal");
    this.init = false;
  }

  async connectedCallback() {
    super.connectedCallback();
    this._initSlider();

    this.cart.addEventListener("cart:rerendered", (e) => {
      clearTimeout(this.cartTimoutId);
      this.cartTimoutId = setTimeout(this._fetchItems.bind(this), 1000);
    });

    this.querySelectorAll(".js-open-series-modal").forEach((button) =>
      this.modal.addOpenTrigger("click", button)
    );
    this.querySelectorAll(".js-close-series-modal").forEach((button) =>
      this.modal.addCloseTrigger("click", button)
    );
  }

  async _updateContent() {
    this.setAttribute("aria-busy", true);

    const carouselItems = this.querySelector(".js-carousel-items");
    const modalItems = this.querySelector(".js-modal-items");
    const totalPriceEls = this.querySelectorAll(".js-total-price");
    const booksCountEls = this.querySelectorAll(".js-books-count");
    const newCarouselItems = document.createElement("DIV");
    const newModalItems = document.createElement("DIV");
    let newTotalPrice = 0;
    let newBooksCount = 0;

    for (const batch of this._sectionBatches) {
      const curCarouselItems = batch.querySelector(".js-carousel-items");
      const curModalItems = batch.querySelector(".js-modal-items");
      const curTotalPrice =
        parseInt(batch.querySelector(".js-total-price")?.dataset.totalPrice) || 0;
      const curBooksCount =
        parseInt(batch.querySelector(".js-books-count")?.dataset.booksCount) || 0;

      newCarouselItems.insertAdjacentHTML("beforeend", curCarouselItems.innerHTML);
      newModalItems.insertAdjacentHTML("beforeend", curModalItems.innerHTML);
      newTotalPrice += curTotalPrice;
      newBooksCount += curBooksCount;
    }

    if (Number.parseInt(newBooksCount) === 0) {
      this.setAttribute("aria-hidden", true);
      return;
    } else {
      this.setAttribute("aria-hidden", false);
    }

    if (!this.init) {
      carouselItems.innerHTML = newCarouselItems.innerHTML;
      this.swiper?.update();
      this.init = true;
    }
    if(this.splide) {
      this.splide.refresh();
    }
    modalItems.innerHTML = newModalItems.innerHTML;
    totalPriceEls.forEach((el) => (el.textContent = formatMoney(newTotalPrice)));
    booksCountEls.forEach((el) => (el.textContent = newBooksCount));

    this.setAttribute("aria-busy", false);
  }

  async _initSlider() {
    this.splideEl = this.querySelector(".splide");

    let options = {
      arrows: false,
      drag: "free",
      pagination: false,
      perPage: 5,
      authHeight: true,
      gap: 16,
      breakpoints: {
        640: {
          destroy: true
        },
        768: {
          perPage: 3,
        },
        900: {
          perPage: 2,
        },
        1140: {
          perPage: 3,
        },
        1280: {
          perPage: 4,
        },
      }
    };

    this.splide = await CarouselManager.create(this.splideEl, options);

    this._initWheel();
    if (this.dataset.arrows === "true") {
      this._initSliderArrows();
    }
  }

  _initWheel() {
    let lastTime = 0;
    const min = this.splide.options.wheelMinThreshold || 0;
    const sleep = this.splide.options.wheelSleep || 80;
  
    this.splideEl.addEventListener("wheel", (e) => {
      if(window.innerWidth < 640) return;
      if (e.cancelable) {
        let delta = e.deltaX;
  
        if (e.shiftKey && delta === 0) {
          delta = e.deltaY;
        }
  
        const backwards = delta < 0;
        const timeStamp = Date.now();
  
        if (Math.abs(delta) > min && timeStamp - lastTime > sleep) {
          this.splide.go(backwards ? "<" : ">");
          lastTime = timeStamp;
          e.preventDefault();
        }
      }
    });
  }

  _initSliderArrows() {
    const sliderNavEl = this.querySelector(".slider-nav");
    if (sliderNavEl) {
      const navNextBtn = sliderNavEl.querySelector(".nav-next");
      const navPrevBtn = sliderNavEl.querySelector(".nav-prev");

      const maxIndex = () => {
        const maxIndex = this.splide.Components.Controller.getEnd() - this.visibleSlides + 1;
        const currentIndex = this.splide.Components.Controller.getIndex();
        
        if(maxIndex === currentIndex) {
          return true;
        } else if(maxIndex < currentIndex) {
          this.splide.Components.Controller.setIndex(maxIndex);
          return true;
        } else {
          return false;
        }
      }

      navNextBtn.addEventListener("click", () => {
        if(maxIndex()) return;
        this.splide.go(">");
      });

      navPrevBtn.addEventListener("click", () => {
        maxIndex();
        this.splide.go("<");
      });
    } else {
      console.warn("No slider nav found");
    }
  }

  get visibleSlides() {
    if (!this.splide) return 1;
    if(!this._visibleSlides || this._windowWidth !== window.innerWidth) {
      this._windowWidth = window.innerWidth;
      this._visibleSlides = this.splide.root.querySelectorAll(".splide__slide.is-visible").length
    }
    return this._visibleSlides;
  }

  get productId() {
    return this.dataset.productId;
  }
}

class SeriesCarouselItemsForm extends BookSeriesItemsForm {
  constructor() {
    super();

    this.modal = this.closest("series-carousel-modal");
    this.totalPriceEl = this.querySelector(".js-total-price");
    this.booksCountEl = this.querySelector(".js-books-count");
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("series-carousel-items:update", this._onUpdate.bind(this));
  }

  async _addToCart() {
    const items = this.querySelectorAll('.js-series-item[aria-disabled="false"]');
    const datalayerItems = new Array();
    const klaviyoItems = new Array();
    const atcItems = new Array();
    items.forEach((item) => {
      datalayerItems.push(item.atcDatalayer);
      klaviyoItems.push(item.atcKlaviyoEventData);

      const properties = {
        Auteur: item.author,
      };

      if (this._collection?.title) {
        properties._collectionTitle = item.collection.title;
      }

      if (this._collection?.id) {
        properties._collectionId = item.collection.id;
      }

      atcItems.push({
        id: Number.parseInt(item.variantId),
        quantity: 1,
        properties
      });
    });

    document.dispatchEvent(new CustomEvent("theme:loading:start"));
    try {
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items: atcItems }),
      });

      if (response.ok) {
        this.dispatchEvent(
          new CustomEvent("product:added", {
            bubbles: true,
            detail: {
              button: this,
              variant: null,
              quantity: atcItems.length,
            },
          })
        );
        datalayerItems.forEach(item => {
          window.dataLayer.push({
            'cloud_retail': ''
          });
          window.dataLayer.push(item);
        });
        klaviyoItems.forEach((item, index) => {
          setTimeout(() => {
            console.log("push item", item);
            window.jollyFunctions.klaviyoTrackAddToCart(item);
          }, index * 1000); // 1000 ms = 1 second delay per item
        });
      }
    } catch (error) {
      console.error(error.message);
    }
  }

  async _onSubmit(e) {
    e.preventDefault();
    this.modal.close();
    await this._addToCart();
  }

  _onUpdate(e) {
    let amount = 0;
    let count = 0;
    Array.from(this.querySelectorAll("series-carousel-item"))
      .filter((item) => item.getAttribute("aria-disabled") === "false")
      .forEach((item) => {
        amount += Number.parseInt(item.dataset.price);
        count++;
      });

    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = formatMoney(amount);
    }
    if (this.booksCountEl) {
      this.booksCountEl.textContent = count;
    }
  }
}

class SeriesCarouselItem extends BookSeriesItem {
  constructor() {
    super();

    this.removeButton = this.querySelector(".js-remove-item");
  }

  connectedCallback() {
    this.removeButton.addEventListener("click", this._onRemoveButtonClick.bind(this));
  }

  _onRemoveButtonClick(e) {
    const disable =
      this.form.querySelectorAll('series-carousel-item:not([aria-disabled="true"])').length > 1;
    if (disable) {
      this.disable();
      this.form.dispatchEvent(new CustomEvent("series-carousel-items:update", { bubbles: true }));
    }
  }

  get atcDatalayer() {
    const ga_add_to_cart = {
      'event': 'ga_add_to_cart',
      'cloud_retail': {
        'eventType': 'add-to-cart',
        'attributionToken': sessionStorage.getItem('attributionToken') ? sessionStorage.getItem('attributionToken') : null,
        'visitorId': localStorage.getItem("visitorId"),
        'productDetails': [{
          'product': {
            'id': `${this.dataset.barcode}`
          },
          'quantity': 1
        }],
        'cartId': `${window.cartToken}`,
        'userInfo': {
          'userId': `${window.userId}`
        }
      },
      'productDetails': [{
        'product': {
          'id': `${this.dataset.barcode}`,
          'L1_main_category': `${this.dataset.l1}`,
          'L2_main_category': `${this.dataset.l2}`,
          'pricing_group': `${this.dataset.pricingGroup}`,
          'pricing_stage': `${this.dataset.pricingStage}`
        },
        'quantity': 1
      }],
    };

    if(this.dataset.carouselId) {
      ga_add_to_cart.productDetails[0].product.carousel_id = parseInt(this.dataset.carouselId);
    }
    if(this.dataset.carouselName) {
      ga_add_to_cart.productDetails[0].product.carousel_name = this.dataset.carouselName;
    }
    if(this.dataset.positionInCarousel) {
      ga_add_to_cart.productDetails[0].product.position_in_carousel = parseInt(this.dataset.positionInCarousel);
    }
    return ga_add_to_cart;
  }
}

class SeriesItemStateSelector extends GDropdown {
  constructor() {
    super();

    this.itemEl = this.closest("series-carousel-item");
    this.variantPrices = JSON.parse(this.itemEl.querySelector("#variant-prices").innerHTML);
    this.itemPriceEl = this.itemEl.querySelector(".js-item-price");
  }

  connectedCallback() {
    this.addEventListener("click", (e) => {
      if (this.getAttribute("aria-expanded") === "false") {
        this.open();
      } else {
        this.close();
      }
    });

    this._items.forEach((item) =>
      item.addEventListener("click", (e) => {
        this.value = item.dataset.value;
      })
    );

    window.addEventListener("click", (e) => {
      if (e.target.closest("series-item-state-selector") != this) {
        this.close();
      }
    });
  }

  set value(value) {
    super.value = value;

    this.itemPriceEl.textContent = formatMoney(this.variantPrices[value]);
    this.itemEl.dataset.price = this.variantPrices[value];
    this.dispatchEvent(new CustomEvent("series-carousel-items:update", { bubbles: true }));
  }
}

window.customElements.define("series-carousel-modal", SeriesCarouselModal);
window.customElements.define("series-carousel", SeriesCarousel);
window.customElements.define("series-carousel-items-form", SeriesCarouselItemsForm, {
  extends: "form",
});
window.customElements.define("series-carousel-item", SeriesCarouselItem);
window.customElements.define("series-item-state-selector", SeriesItemStateSelector);
